/**
 * Drag-and-drop file input.
 *
 * The visible drop area is a label bound to a real `<input type="file">` rather than a div with
 * handlers. That is what makes it work with the keyboard and with a screen reader, and it is what
 * keeps it working when someone would rather click Browse than drag — which, for an administrator
 * uploading a circular from a network share, is most of the time.
 *
 * Type and size are checked here for the user's benefit only. The server checks again and its
 * check is the one that counts: anything enforced solely in a browser is not enforced.
 */

import { useId, useRef, useState, type DragEvent } from "react";

import { cx } from "./cx";

export interface FileDropzoneProps {
  label: string;
  hint?: string;
  /** Accept attribute *and* the client-side guard, e.g. `[".pdf", ".docx"]`. */
  accept: string[];
  maxBytes?: number;
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  /** Localized message for a rejected file. Receives the filename. */
  rejectMessage: (filename: string) => string;
  className?: string;
}

export function FileDropzone({
  label,
  hint,
  accept,
  maxBytes,
  onFiles,
  multiple = false,
  rejectMessage,
  className,
}: FileDropzoneProps): JSX.Element {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);

  function accepted(files: FileList | File[]): File[] {
    const good: File[] = [];
    const bad: string[] = [];
    for (const file of Array.from(files)) {
      const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
      const tooBig = maxBytes !== undefined && file.size > maxBytes;
      if (!accept.includes(extension) || tooBig) bad.push(file.name);
      else good.push(file);
    }
    setRejected(bad);
    return good;
  }

  function onDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setOver(false);
    const good = accepted(event.dataTransfer.files);
    if (good.length) onFiles(good);
  }

  return (
    <div className={cx("flex flex-col gap-2", className)}>
      <label
        htmlFor={inputId}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cx(
          "flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed",
          "px-6 py-8 text-center",
          "focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary",
          over
            ? "border-primary bg-primarySubtle"
            : "border-borderStrong bg-surfaceSunken hover:border-primary",
        )}
      >
        <span className="text-body-md text-textPrimary">{label}</span>
        {hint ? <span className="text-label-md text-textMuted">{hint}</span> : null}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple={multiple}
          accept={accept.join(",")}
          // Hidden from sight, not from assistive technology or the keyboard.
          className="sr-only"
          onChange={(event) => {
            const good = accepted(event.target.files ?? []);
            if (good.length) onFiles(good);
            // Reset so selecting the same file twice fires again — the retry case.
            event.target.value = "";
          }}
        />
      </label>

      {rejected.length > 0 ? (
        <ul role="alert" className="flex flex-col gap-1">
          {rejected.map((name) => (
            <li key={name} className="text-label-md text-statusDanger">
              {rejectMessage(name)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
